import {
	AbsoluteFill,
	Audio,
	OffthreadVideo,
	Sequence,
	useVideoConfig,
} from "remotion";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useMemo } from "react";

export default function VideoRenderer({
										  originalVideo,
										  audioFiles = [],
										  backgroundMusic,
										  fitAudio = false,
										  currentMode = "translated",
										  isCaptionEnabled = false,
										  isWatermarkEnabled = false,
									  }) {
	const { fps, durationInFrames, width } = useVideoConfig();

	// Memoized audio file data with optimized precision
	const audioFileData = useMemo(() => {
		if (!audioFiles.length) return [];

		return audioFiles.map((segment) => {
			const duration = segment.end - segment.start;
			const originalDuration = segment.originalEnd - segment.originalStart || duration;

			return {
				...segment,
				startFrames: Math.round(fps * segment.start),
				endFrames: Math.round(fps * segment.end),
				timestampDuration: Math.round(duration * 1000) / 1000,
				originalDuration: Math.round(originalDuration * 1000) / 1000,
				durationFrames: Math.round(fps * Math.max(duration, 0.1)),
			};
		});
	}, [audioFiles, fps]);

	const calculatePlaybackRate = useCallback(
	(segment) => {
		if (!fitAudio) {
			return 1.0;
		}

		const timeSlotDuration = segment.timestampDuration;
		const audioDuration = segment.duration || segment.originalDuration;

		if (!audioDuration || audioDuration <= 0) {
			return 1.0;
		}

		const requiredRate = audioDuration / timeSlotDuration;
		const clampedRate = Math.min(Math.max(requiredRate, 0.50), 4);

		return Math.round(clampedRate * 100) / 100;
	},
	[fitAudio]
	);

	const WatermarkComponent = useMemo(() => {
		if (!isWatermarkEnabled) return null;

		return (
		<AbsoluteFill className="pointer-events-none flex justify-center items-center">
			<div
			className="text-5xl font-bold text-white opacity-40"
			style={{
				WebkitTextStroke: "1px rgba(0,0,0,0.5)",
				textShadow: "0 2px 4px rgba(0,0,0,0.5)",
				fontWeight: "bold",
			}}
			>
				AudioDub.ai
			</div>
		</AbsoluteFill>
		);
	}, [isWatermarkEnabled]);

	const captionStyles = useMemo(() => ({
		fontSize: Math.max(width * 0.025, 16),
		maxWidth: `${width * 0.9}px`,
	}), [width]);

	const animationConfig = useMemo(() => ({
		initial: { opacity: 0, y: 50 },
		animate: { opacity: 1, y: 0 },
		exit: { opacity: 0, y: -50 },
		transition: {
			type: "spring",
			stiffness: 120,
			damping: 15,
			mass: 0.8,
		},
	}), []);

	const isTranslatedMode = currentMode === "translated";

	return (
	<>
		<Sequence from={0} durationInFrames={durationInFrames}>
			{originalVideo && (
			<AbsoluteFill>
				<OffthreadVideo
				muted={isTranslatedMode}
				startFrom={0}
				src={originalVideo}
				pauseWhenBuffering={false}
				transparent={false}
				onError={(error) => {
					console.error("Video Error:", error.message || error);
				}}
				/>

				{isTranslatedMode && backgroundMusic && (
				<Audio
				src={backgroundMusic}
				pauseWhenBuffering
				preload="metadata"
				onError={(error) => {
					console.error("Background Music Error:", error.message || error);
				}}
				/>
				)}
			</AbsoluteFill>
			)}
			{WatermarkComponent}
		</Sequence>

		{audioFileData.map((audio, index) => {
			const playbackRate = calculatePlaybackRate(audio);

			return (
			<Sequence
			key={`audio-${audio.speaker_user || index}-${audio.startFrames}`}
			from={audio.startFrames}
			durationInFrames={audio.durationFrames}
			>
				{isCaptionEnabled && (
				<AbsoluteFill className="flex justify-end items-center py-5 pointer-events-none">
					<AnimatePresence mode="wait">
						<motion.div
						style={captionStyles}
						className="font-medium text-center inline-block rounded-lg py-2 px-4 bg-black/30 text-white backdrop-blur-sm shadow-lg"
						{...animationConfig}
						>
							<p className="leading-tight">
								{isTranslatedMode ? audio.translated : audio.original}
							</p>
						</motion.div>
					</AnimatePresence>
				</AbsoluteFill>
				)}

				{isTranslatedMode && audio.url && (
				<Audio
					src={audio.url}
					startFrom={0}
					endAt={audio.durationFrames}
					pauseWhenBuffering={false}
					preload="metadata"
					playbackRate={playbackRate}
					volume={1.0}
					onError={(error) => {
						console.error(`Audio Error for segment ${index}:`, error.message || error);
					}}
				/>
				)}
			</Sequence>
			);
		})}
	</>
	);
}